import { useEffect, useRef } from 'react';
import Lenis from '@studio-freight/lenis';

/**
 * Hook that initializes Lenis smooth scrolling.
 *
 * - Duration: 1.2s
 * - Custom easing (exponential ease-out)
 * - Smooth wheel only (touch uses native)
 * - Respects `prefers-reduced-motion` by skipping init entirely
 *
 * Returns the Lenis instance (ref.current) so callers can programmatically
 * scroll via `lenisRef.current?.scrollTo(...)`.
 */
const useLenis = () => {
  const lenisRef = useRef(null);

  useEffect(() => {
    // Respect prefers-reduced-motion: skip Lenis completely
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      return undefined;
    }

    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      smoothTouch: false,
    });

    lenisRef.current = lenis;

    // Expose on window for other components (ScrollToTop, anchor links)
    if (typeof window !== 'undefined') {
      window.__lenis = lenis;
    }

    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
      if (typeof window !== 'undefined' && window.__lenis === lenis) {
        delete window.__lenis;
      }
    };
  }, []);

  return lenisRef;
};

export default useLenis;
